<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { LoadingView, dateHelpers } from '@hfc/ui-core';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { AppTypeDentalOrVision } from '@/application/shared/domain/DentalAndVisionApplication';
import { MEMBER_TYPES } from '@hfc/common/src/types/MemberTypes';
import { Applicant } from '@/application/shared/domain/Applicant';
import { PlanResponse } from '@/planList/domain/PlanResponse';
import PlanListItem from '@/planList/infrastructure/components/PlanListItem.vue';
import { useUrlHelper } from '@/shared/infrastructure/composition/useUrlHelper';
import { useQuoteStore } from '@/quote/infrastructure/composition/useQuoteStore';

const { quoteStore } = useQuoteStore();

const { getRouteNameByAppType } = useUrlHelper();
const { fromTimestampToFormatted } = dateHelpers();

const props = withDefaults(defineProps<{
  applicationType: APPLICATION_TYPES;
  planList: Array<PlanResponse>;
  loadingPlans: boolean;
  applicants: Array<Applicant>;
  viewDetailsButton?: boolean
  compact?: boolean;
}>(), {
  viewDetailsButton: false,
});

const quoteRouteName = ref<string>('');

const plansHMO = computed(() => props.planList?.filter(plan => plan.type === 'HMO') as Array<PlanResponse>);
const plansPPO = computed(() => props.planList?.filter(plan => plan.type === 'PPO') as Array<PlanResponse>);

const applicant = computed(() => props.applicants?.find((applicant: Applicant) => applicant.type === MEMBER_TYPES.APPLICANT) || null);
const spouse = computed(() => props.applicants?.find((applicant: Applicant) => applicant.type === MEMBER_TYPES.SPOUSE) || null);
const otherApplicants = computed(() => props.applicants?.filter((applicant: Applicant) => applicant.type === MEMBER_TYPES.CHILD || applicant.type === MEMBER_TYPES.DEPENDENT) || null);

interface EmitsInterface {
  (event: 'selectPlan', plan: PlanResponse): void,
  (event: 'viewDetails', plan: PlanResponse): void,
}

const emits = defineEmits<EmitsInterface>();

const setSelectedPlan = (plan: PlanResponse): void => {
  emits('selectPlan', plan);
};

const setViewDetails = (plan: PlanResponse): void => {
  emits('viewDetails', plan);
};

onMounted(async () => {
  const response = await quoteStore.getQuoteInfo(props.applicationType as AppTypeDentalOrVision);
  if (response.success && response.data) {
    const quote = response.data;
    quoteRouteName.value = getRouteNameByAppType(props.applicationType, quote.isSenior ? 'QuoteSenior' : 'Quote');
  }
});
</script>

<template>
  <div v-if="!loadingPlans" class="flex flex-row py-6">
    <div class="flex flex-wrap w-full text-md justify-end">
      <span class="font-semibold whitespace-nowrap">Who is being quoted? -&nbsp;</span>
      <span v-if="applicant != null" class="font-medium whitespace-nowrap">
        Self ({{ fromTimestampToFormatted(applicant.date_of_birth, 'MM/DD/YYYY') }}),&nbsp;
      </span>
      <span v-if="spouse != null" class="font-medium whitespace-nowrap">
        Spouse ({{ fromTimestampToFormatted(spouse.date_of_birth, 'MM/DD/YYYY') }}),&nbsp;
      </span>
      <span v-if="otherApplicants != null && otherApplicants.length > 0" class="font-medium whitespace-nowrap">
        {{ otherApplicants.length }} other applicant{{ otherApplicants.length > 1 ? 's' : '' }},&nbsp;
      </span>
      <router-link class="font-semibold whitespace-nowrap" :to="{ name: quoteRouteName }">
        Edit Quote Info
      </router-link>
    </div>
  </div>
  <div class="flex flex-col px-10 py-8 bg-white rounded-md min-h-[25rem]">
    <div v-if="!loadingPlans" class="flex flex-col md:flex-row justify-center">
      <div v-if="plansHMO.length > 0" class="basis-full flex flex-col overflow-x-auto" :class="{ 'md:basis-1/2 lg:basis-2/6': plansPPO.length }">
        <div class="flex w-full px-4 items-center mb-4">
          <div class="flex-1 border-t-[3px] border-blue-400"></div>
          <div class="mx-4 font-semibold text-blue-500">HMO</div>
          <div class="flex-1 border-t-[3px] border-blue-400"></div>
        </div>
        <div class="flex w-full overflow-x-auto items-center flex-col md:flex-row gap-4 md:gap-0 pb-4">
          <PlanListItem
            v-for="(plan, i) in plansHMO"
            :plan="plan"
            :cols="1"
            :key="i"
            :view-details-button="viewDetailsButton"
            :application-type="applicationType"
            :compact="compact"
            @selectPlan="setSelectedPlan(plan)"
            @setDetailsModal="setViewDetails" />
        </div>
      </div>
      <div v-if="plansPPO.length > 0" class="basis-full flex flex-col overflow-x-auto" :class="{ 'md:basis-1/2 lg:basis-4/6': plansHMO.length }">
        <div class="flex w-full px-4 items-center mb-4">
          <div class="flex-1 border-t-[3px] border-gray-400"></div>
          <div class="mx-4 font-semibold text-gray-500">PPO</div>
          <div class="flex-1 border-t-[3px] border-gray-400"></div>
        </div>
        <div class="flex w-full overflow-x-auto items-center flex-col md:flex-row gap-4 md:gap-0 pb-4">
          <PlanListItem
            v-for="(plan, i) in plansPPO"
            :plan="plan"
            :cols="2"
            :key="i"
            :view-details-button="viewDetailsButton"
            :application-type="applicationType"
            :compact="compact"
            @selectPlan="setSelectedPlan(plan)"
            @setDetailsModal="setViewDetails" />
        </div>
      </div>
    </div>
    <div v-if="!loadingPlans && planList.length === 0" class="flex flex-col justify-center items-center h-96">
      No plans found for the selected family.
    </div>
    <div v-if="loadingPlans" class="h-96">
      <LoadingView class="bg-opacity-50" />
    </div>
  </div>
</template>
